import React, { useEffect, useRef } from 'react'
import * as janusvideo from 'janus-video-wrapper'
import { RootReducer } from '../redux/rootReducer'
import { useDispatch, useSelector } from 'react-redux'
import { SET_CONNECTION_STATE } from '../redux/janusReducer'
interface Props {
  roomId: number
  localVideoRef: React.RefObject<HTMLVideoElement>
  remoteVideoRef: React.RefObject<HTMLVideoElement>
}

const JanusRoomComponent = ({ remoteVideoRef, localVideoRef, roomId }: Props) => {
  const janusRoomRef = useRef<janusvideo.Janus.JanusRoom | null>(null)
  const dispatch = useDispatch()
  const invisibleLocalVideo = useRef<HTMLVideoElement>(null)
  const janusSecret = useSelector((state: RootReducer) => state.tutorReducer.janus_secret)
  const cameraId = useSelector((state: RootReducer) => state.tutorChannelReducer.selectedCameraId)
  const tutorId = useSelector((state: RootReducer) => state.tutorReducer.id)
  const microphoneId = useSelector((state: RootReducer) => state.tutorChannelReducer.selectedMicrophoneId)
  const microphoneEnabled = useSelector((state: RootReducer) => state.lessonToolsReducer.microphoneEnabled)
  const cameraEnabled = useSelector((state: RootReducer) => state.lessonToolsReducer.cameraEnabled)

  useEffect(() => {
    if (!janusRoomRef.current || !janusRoomRef.current.plugin) {
      return
    }
    const janusPlugin = janusRoomRef.current.plugin
    const joinedInfo = janusRoomRef.current.joinedInfo
    if (!joinedInfo) {
      return
    }
    const deviceOptions = JSON.stringify({
      audio: microphoneId ? { deviceId: { exact: microphoneId } } : true,
      video: cameraId ? { deviceId: { exact: cameraId } } : true
    })

    console.log('🔄 Renegotiating video settings:', deviceOptions)
    // 🛠️ Call processLocalVideo to renegotiate the media stream
    janusPlugin
      .processLocalVideo({ audio: microphoneEnabled, video: cameraEnabled }, joinedInfo, deviceOptions)
      .then(() => {
        console.log(`✅ Successfully ${cameraEnabled ? 'enabled' : 'disabled'} video`)
      })
  }, [cameraEnabled, microphoneEnabled, cameraId, microphoneId])

  useEffect(() => {
    if (localVideoRef.current && localVideoRef.current.srcObject) {
      const videoTrack = (localVideoRef.current.srcObject as MediaStream).getVideoTracks()[0]
      if (videoTrack) {
        videoTrack.enabled = cameraEnabled
      }
    }
  }, [cameraEnabled, localVideoRef])

  useEffect(() => {
    const getMediaStream = async () => {
      if (localVideoRef.current !== null) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: cameraId ? { exact: cameraId } : undefined },
          audio: { deviceId: microphoneId ? { exact: microphoneId } : undefined }
        })
        localVideoRef.current.srcObject = stream
      }
    }
    getMediaStream()
  }, [cameraId, microphoneId, localVideoRef])

  useEffect(() => {
    if (invisibleLocalVideo.current == null) {
      return
    }

    janusRoomRef.current = new janusvideo.Janus.JanusRoom(
      process.env.NODE_ENV === 'development' ? 'ws://localhost:8188' : 'wss://fluent-future.com/janus',
      {
        keepalive: true,
        apisecret: janusSecret
      }
    )

    janusRoomRef.current.eventRoomjoined.on((info: any) => {
      console.log('✅ Joined Room:', info)
      dispatch(SET_CONNECTION_STATE({ state: 'connected' }))
    })

    janusRoomRef.current.eventNeedVideoLocal.on((elementInfo: any) => {
      elementInfo.element = invisibleLocalVideo.current
      setInterval(() => {
        if (invisibleLocalVideo.current) {
          // janus js lib overrides the muted property
          invisibleLocalVideo.current.muted = true
          invisibleLocalVideo.current.volume = 0
        }
      }, 1000)
    })

    janusRoomRef.current.eventNeedVideoRemote.on((event: any) => {
      if (event.feedInfo.display !== `Tutor-${tutorId}`) {
        event.element = remoteVideoRef.current
      }
    })

    janusRoomRef.current
      .joinRoom(
        roomId,
        'Tutor-' + tutorId,
        { ptype: 'publisher', display: `Tutor-${tutorId}`, id: parseInt(`1${tutorId}`) },
        JSON.stringify({
          audio: microphoneId ? { deviceId: { exact: microphoneId } } : true,
          video: cameraId ? { deviceId: { exact: cameraId } } : true
        })
      )
      .then(() => {
        dispatch(SET_CONNECTION_STATE({ state: 'connected' }))
      })
      .catch(() => {
        dispatch(SET_CONNECTION_STATE({ state: 'failed' }))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => {
      if (janusRoomRef.current && janusRoomRef.current.plugin) {
        console.log('Disconnecting from Janus')
        janusRoomRef.current.plugin.disconnect()
      }
    }
  }, [invisibleLocalVideo])

  return (
    <>
      <video className="w-0 h-0 hidden" ref={invisibleLocalVideo} muted />
    </>
  )
}

export default JanusRoomComponent
